/**
 * MeetingAudioRecorder
 * Independent audio recorder for Meeting transcription feature
 * Records at 16kHz, mono, 16-bit PCM WAV format (~2 MB/min)
 * Optimized for AWS Transcribe with chunked uploads
 * 
 * NOTE: This is intentionally separate from NovaAudioRecorder to ensure
 * changes to voice conversation don't affect meeting recording.
 */

export interface AudioChunk {
    blob: Blob;
    duration: number; // Duration in seconds
    chunkIndex: number;
}

export type OnChunkCallback = (chunk: AudioChunk) => void;

// A capture source the browser or OS pulled out from under a LIVE
// recording: the user hit Chrome's "Stop sharing", closed the shared
// tab, unplugged a headset, or the OS revoked the device.
export type CaptureLossKind = 'system_audio' | 'microphone';
export type OnCaptureLossCallback = (kind: CaptureLossKind) => void;

// RMS below this counts as silence. With noiseSuppression on, a quiet
// room sits near 0; normal speech is well above 0.05.
const SILENCE_RMS_THRESHOLD = 0.01;

export default class MeetingAudioRecorder {
    private mediaStream: MediaStream | null = null;
    private audioContext: AudioContext | null = null;
    private mediaStreamSource: MediaStreamAudioSourceNode | null = null;
    private scriptProcessor: ScriptProcessorNode | null = null;
    private isRecording: boolean = false;
    private isPausedFlag: boolean = false;

    // Audio buffer for chunking
    private audioBuffer: Float32Array[] = [];
    private bufferStartTime: number = 0;
    private chunkIndex: number = 0;

    // Configuration
    private readonly sampleRate: number = 16000;
    private readonly chunkDurationMs: number;
    private onChunkReady: OnChunkCallback | null = null;

    // Timer for chunk generation
    private chunkTimer: NodeJS.Timeout | null = null;

    // Optional live-transcript tap (WS-01): receives each ~256 ms frame
    // as 16-bit PCM, in parallel with the chunk buffer. Same data, same
    // callback, so the tap can never desync the recording path. The tap
    // deliberately gets ALL frames, silence included — the server's gap
    // detection relies on frames flowing continuously (never "optimize"
    // silent frames away).
    private pcmTap: ((pcm: Int16Array) => void) | null = null;

    // Silence detection: evaluated per audio frame (frames flow
    // continuously while recording, silence included — see the tap note
    // above). When every frame for silenceThresholdMs stays below
    // SILENCE_RMS_THRESHOLD, onSilence fires ONCE; it re-arms only after
    // sound is heard again, so a dismissed warning doesn't re-fire every
    // threshold of an intentionally quiet stretch. Paused = no frames = no
    // evaluation, so a pause can never trip it.
    private readonly silenceThresholdMs: number;
    private onSilence: (() => void) | null = null;
    private lastSoundAt: number = 0;
    private silenceFired: boolean = false;

    // True only when tab/system audio genuinely reached the merged bus.
    // BOTH startWithTabAudio fallbacks (picker refused/cancelled, or a
    // share that carries no audio track — a screen/window share, or the
    // Teams desktop app) leave this false. The caller MUST surface that:
    // the recording is mic-only, so the remote party is neither captured
    // nor seen by silence detection.
    private systemAudioActive: boolean = false;

    // Mid-recording capture loss. systemAudioActive is otherwise a
    // start-time snapshot, and a share that dies in minute 20 is the
    // same failure as one that never started — the remote party stops
    // being recorded — so it has to be reported the same way.
    private onCaptureLoss: OnCaptureLossCallback | null = null;
    private lostCapture: Set<CaptureLossKind> = new Set();

    constructor(chunkDurationMs: number = 30000, silenceThresholdMs: number = 30000) {
        this.chunkDurationMs = chunkDurationMs;
        this.silenceThresholdMs = silenceThresholdMs;
    }

    /**
     * Attach/detach the continuous-silence callback. Safe at any time;
     * a throwing callback never disturbs the recording path.
     */
    setOnSilence(callback: (() => void) | null): void {
        this.onSilence = callback;
    }

    /**
     * Attach/detach the live PCM tap. Safe at any time; a throwing tap
     * never disturbs the recording path.
     */
    setPcmTap(tap: ((pcm: Int16Array) => void) | null): void {
        this.pcmTap = tap;
    }

    /**
     * Attach/detach the capture-loss callback. Fires at most once per
     * source per recording; a throwing callback never disturbs the
     * recording path.
     */
    setOnCaptureLoss(callback: OnCaptureLossCallback | null): void {
        this.onCaptureLoss = callback;
    }

    /**
     * Whether the remote party's audio is actually on the recorded bus.
     * Only meaningful once start()/startWithTabAudio() has resolved.
     */
    isSystemAudioActive(): boolean {
        return this.systemAudioActive;
    }

    /**
     * Start recording audio
     */
    async start(onChunkReady: OnChunkCallback): Promise<void> {
        try {
            this.onChunkReady = onChunkReady;
            this.chunkIndex = 0;
            this.audioBuffer = [];
            this.isPausedFlag = false;
            this.systemAudioActive = false;
            this.lostCapture.clear();

            // Request microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: this.sampleRate,
                },
            });

            this.watchTracks(this.mediaStream.getAudioTracks(), 'microphone');

            await this.setupAudioProcessing();
            console.log('[MeetingAudioRecorder] Recording started (mic only)');
        } catch (error) {
            console.error('[MeetingAudioRecorder] Failed to start recording:', error);
            throw error;
        }
    }

    /**
     * Start recording with both microphone AND tab/system audio.
     * This is useful for recording Teams/Zoom calls where you want to capture
     * both your voice and the remote participant's audio.
     * 
     * Note: This will prompt the user to select a tab to share.
     */
    async startWithTabAudio(onChunkReady: OnChunkCallback): Promise<void> {
        try {
            this.onChunkReady = onChunkReady;
            this.chunkIndex = 0;
            this.audioBuffer = [];
            this.isPausedFlag = false;
            this.systemAudioActive = false;
            this.lostCapture.clear();

            console.log('[MeetingAudioRecorder] Requesting microphone access...');

            // 1. Get microphone audio
            const micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true, // Enable echo cancellation to prevent double-recording remote audio
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            this.watchTracks(micStream.getAudioTracks(), 'microphone');

            console.log('[MeetingAudioRecorder] Requesting tab audio access...');

            // 2. Get tab/system audio (user will be prompted to select a tab)
            let tabStream: MediaStream;
            try {
                tabStream = await navigator.mediaDevices.getDisplayMedia({
                    video: true, // Some browsers require video, we'll ignore it
                    audio: true, // This captures the tab's audio
                });

                // Stop the video track immediately - we only need audio
                tabStream.getVideoTracks().forEach(track => track.stop());
            } catch (displayError) {
                console.warn('[MeetingAudioRecorder] Tab audio capture failed, falling back to mic only:', displayError);
                // Fallback to mic-only recording
                this.mediaStream = micStream;
                await this.setupAudioProcessing();
                console.log('[MeetingAudioRecorder] Recording started (mic only - fallback)');
                return;
            }

            console.log('[MeetingAudioRecorder] Merging audio streams...');

            // 3. Create audio context and merge both streams
            this.audioContext = new AudioContext({ sampleRate: this.sampleRate });

            // Create a destination for the merged audio
            const destination = this.audioContext.createMediaStreamDestination();

            // Create source nodes for both streams
            const micSource = this.audioContext.createMediaStreamSource(micStream);
            const tabAudioTracks = tabStream.getAudioTracks();

            // Connect microphone to destination
            micSource.connect(destination);

            // Connect tab audio to destination if available
            if (tabAudioTracks.length > 0) {
                const tabAudioStream = new MediaStream(tabAudioTracks);
                const tabSource = this.audioContext.createMediaStreamSource(tabAudioStream);
                tabSource.connect(destination);
                this.systemAudioActive = true;
                this.watchTracks(tabAudioTracks, 'system_audio');
                console.log('[MeetingAudioRecorder] Tab audio connected');
            } else {
                console.warn('[MeetingAudioRecorder] No tab audio tracks found');
            }

            // Use the merged stream for recording
            this.mediaStream = destination.stream;

            // Store original streams for cleanup
            (this as any)._micStream = micStream;
            (this as any)._tabStream = tabStream;

            await this.setupAudioProcessing();
            console.log('[MeetingAudioRecorder] Recording started (mic + tab audio)');
        } catch (error) {
            console.error('[MeetingAudioRecorder] Failed to start recording with tab audio:', error);
            throw error;
        }
    }

    /**
     * Common audio processing setup
     */
    private async setupAudioProcessing(): Promise<void> {
        // Create audio context if not already created
        if (!this.audioContext) {
            this.audioContext = new AudioContext({ sampleRate: this.sampleRate });
        }

        this.mediaStreamSource = this.audioContext.createMediaStreamSource(this.mediaStream!);

        const actualSampleRate = this.audioContext.sampleRate;
        console.log(`[MeetingAudioRecorder] Sample rate: ${actualSampleRate}`);

        // Create script processor for audio processing
        // Note: ScriptProcessor is deprecated but widely supported
        // Buffer size of 4096 provides good balance between latency and performance
        this.scriptProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);

        // Process audio data
        this.scriptProcessor.onaudioprocess = (event: AudioProcessingEvent) => {
            if (!this.isRecording || this.isPausedFlag) return;

            const inputData = event.inputBuffer.getChannelData(0);

            // Copy the data (inputBuffer is reused)
            const dataCopy = new Float32Array(inputData.length);
            dataCopy.set(inputData);

            this.audioBuffer.push(dataCopy);

            if (this.pcmTap) {
                try {
                    this.pcmTap(this.float32ToInt16(dataCopy));
                } catch (tapError) {
                    console.error('[MeetingAudioRecorder] PCM tap error (recording unaffected):', tapError);
                }
            }

            this.detectSilence(dataCopy);
        };

        // Connect the audio graph
        this.mediaStreamSource.connect(this.scriptProcessor);
        this.scriptProcessor.connect(this.audioContext.destination);

        this.isRecording = true;
        this.bufferStartTime = Date.now();
        this.lastSoundAt = Date.now();
        this.silenceFired = false;

        // Set up chunk timer
        this.chunkTimer = setInterval(() => {
            this.generateAndSendChunk();
        }, this.chunkDurationMs);
    }

    /**
     * Pause recording: flush the buffered audio as a chunk (so it is
     * durable during the pause), stop the chunk timer, and suspend the
     * audio graph. The mic/tab streams stay open so no permission
     * re-prompt is needed on resume, and the browser's recording
     * indicator honestly stays visible.
     */
    async pause(): Promise<void> {
        if (!this.isRecording || this.isPausedFlag) return;

        this.isPausedFlag = true;

        if (this.chunkTimer) {
            clearInterval(this.chunkTimer);
            this.chunkTimer = null;
        }

        // Flush what we have so a crash during a long pause loses nothing
        this.generateAndSendChunk();

        if (this.audioContext && this.audioContext.state === 'running') {
            try {
                await this.audioContext.suspend();
            } catch (err) {
                console.error('[MeetingAudioRecorder] Failed to suspend audio context:', err);
            }
        }

        console.log('[MeetingAudioRecorder] Recording paused');
    }

    /**
     * Resume a paused recording.
     */
    async resume(): Promise<void> {
        if (!this.isRecording || !this.isPausedFlag) return;

        if (this.audioContext && this.audioContext.state === 'suspended') {
            try {
                await this.audioContext.resume();
            } catch (err) {
                console.error('[MeetingAudioRecorder] Failed to resume audio context:', err);
            }
        }

        this.isPausedFlag = false;
        this.bufferStartTime = Date.now();
        // Don't let the pause itself count as silence
        this.lastSoundAt = Date.now();

        if (this.chunkTimer) clearInterval(this.chunkTimer);
        this.chunkTimer = setInterval(() => {
            this.generateAndSendChunk();
        }, this.chunkDurationMs);

        console.log('[MeetingAudioRecorder] Recording resumed');
    }

    /**
     * Stop recording and return final chunk
     */
    async stop(): Promise<AudioChunk | null> {
        if (!this.isRecording) return null;

        this.isRecording = false;
        this.isPausedFlag = false;

        // Clear chunk timer
        if (this.chunkTimer) {
            clearInterval(this.chunkTimer);
            this.chunkTimer = null;
        }

        // Generate final chunk with remaining audio
        const finalChunk = this.generateChunk();

        // Cleanup resources
        if (this.scriptProcessor) {
            this.scriptProcessor.disconnect();
            this.scriptProcessor = null;
        }

        if (this.mediaStreamSource) {
            this.mediaStreamSource.disconnect();
            this.mediaStreamSource = null;
        }

        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }

        // Cleanup additional streams from tab audio capture
        if ((this as any)._micStream) {
            (this as any)._micStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            (this as any)._micStream = null;
        }
        if ((this as any)._tabStream) {
            (this as any)._tabStream.getTracks().forEach((track: MediaStreamTrack) => track.stop());
            (this as any)._tabStream = null;
        }

        if (this.audioContext) {
            await this.audioContext.close();
            this.audioContext = null;
        }

        this.audioBuffer = [];
        console.log('[MeetingAudioRecorder] Recording stopped');

        return finalChunk;
    }

    /**
     * Generate chunk from current buffer and send via callback
     */
    private generateAndSendChunk(): void {
        const chunk = this.generateChunk();
        if (chunk && this.onChunkReady) {
            this.onChunkReady(chunk);
        }
    }

    /**
     * Generate WAV chunk from current audio buffer
     */
    private generateChunk(): AudioChunk | null {
        if (this.audioBuffer.length === 0) {
            return null;
        }

        // Calculate total samples
        const totalSamples = this.audioBuffer.reduce((acc, buf) => acc + buf.length, 0);

        // Merge all buffers into one
        const mergedBuffer = new Float32Array(totalSamples);
        let offset = 0;
        for (const buffer of this.audioBuffer) {
            mergedBuffer.set(buffer, offset);
            offset += buffer.length;
        }

        // Convert to 16-bit PCM
        const pcmData = this.float32ToInt16(mergedBuffer);

        // Create WAV file with header
        const wavBlob = this.createWavBlob(pcmData);

        // Calculate duration
        const durationSeconds = totalSamples / this.sampleRate;

        const chunk: AudioChunk = {
            blob: wavBlob,
            duration: durationSeconds,
            chunkIndex: this.chunkIndex,
        };

        // Reset buffer for next chunk
        this.audioBuffer = [];
        this.chunkIndex++;
        this.bufferStartTime = Date.now();

        console.log(`[MeetingAudioRecorder] Generated chunk ${chunk.chunkIndex}, duration: ${durationSeconds.toFixed(2)}s, size: ${wavBlob.size} bytes`);

        return chunk;
    }

    /**
     * Report a capture source dying under a live recording.
     *
     * Our own teardown calls track.stop(), which per spec does NOT fire
     * 'ended' — so anything arriving here is the browser or the OS
     * taking the source away ("Stop sharing", shared tab closed, headset
     * unplugged, device revoked).
     */
    private watchTracks(tracks: MediaStreamTrack[], kind: CaptureLossKind): void {
        tracks.forEach((track) => {
            track.addEventListener('ended', () => this.handleCaptureLoss(kind));
        });
    }

    private handleCaptureLoss(kind: CaptureLossKind): void {
        if (!this.isRecording) return;
        if (this.lostCapture.has(kind)) return;
        this.lostCapture.add(kind);
        if (kind === 'system_audio') {
            // No longer a truthful claim: the remote party stopped
            // reaching the bus the moment that track ended.
            this.systemAudioActive = false;
        }
        console.warn(`[MeetingAudioRecorder] capture lost mid-recording: ${kind}`);
        try {
            this.onCaptureLoss?.(kind);
        } catch (lossError) {
            console.error('[MeetingAudioRecorder] onCaptureLoss error (recording unaffected):', lossError);
        }
    }

    /**
     * Per-frame silence tracking — see the field docs. Never throws
     * into the audio callback.
     */
    private detectSilence(frame: Float32Array): void {
        if (!this.onSilence) return;

        let sumSquares = 0;
        for (let i = 0; i < frame.length; i++) {
            sumSquares += frame[i] * frame[i];
        }
        const rms = Math.sqrt(sumSquares / frame.length);
        const now = Date.now();

        if (rms >= SILENCE_RMS_THRESHOLD) {
            this.lastSoundAt = now;
            this.silenceFired = false;
        } else if (!this.silenceFired && now - this.lastSoundAt >= this.silenceThresholdMs) {
            this.silenceFired = true;
            try {
                this.onSilence();
            } catch (silenceError) {
                console.error('[MeetingAudioRecorder] onSilence error (recording unaffected):', silenceError);
            }
        }
    }

    /**
     * Convert Float32Array to Int16Array (16-bit PCM)
     */
    private float32ToInt16(float32Array: Float32Array): Int16Array {
        const int16Array = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            // Clamp to [-1, 1] and convert to 16-bit integer
            const s = Math.max(-1, Math.min(1, float32Array[i]));
            int16Array[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return int16Array;
    }

    /**
     * Create a WAV file blob with proper header
     */
    private createWavBlob(pcmData: Int16Array): Blob {
        const numChannels = 1;
        const bitsPerSample = 16;
        const byteRate = this.sampleRate * numChannels * (bitsPerSample / 8);
        const blockAlign = numChannels * (bitsPerSample / 8);
        const dataSize = pcmData.length * (bitsPerSample / 8);
        const fileSize = 44 + dataSize; // 44-byte header + data

        const buffer = new ArrayBuffer(fileSize);
        const view = new DataView(buffer);

        // RIFF chunk descriptor
        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, fileSize - 8, true); // File size - 8 bytes
        this.writeString(view, 8, 'WAVE');

        // fmt sub-chunk
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
        view.setUint16(20, 1, true); // AudioFormat (1 = PCM)
        view.setUint16(22, numChannels, true);
        view.setUint32(24, this.sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, bitsPerSample, true);

        // data sub-chunk
        this.writeString(view, 36, 'data');
        view.setUint32(40, dataSize, true);

        // Write PCM data
        const pcmOffset = 44;
        for (let i = 0; i < pcmData.length; i++) {
            view.setInt16(pcmOffset + i * 2, pcmData[i], true);
        }

        return new Blob([buffer], { type: 'audio/wav' });
    }

    /**
     * Write string to DataView
     */
    private writeString(view: DataView, offset: number, string: string): void {
        for (let i = 0; i < string.length; i++) {
            view.setUint8(offset + i, string.charCodeAt(i));
        }
    }

    /**
     * Check if recording is active
     */
    isActive(): boolean {
        return this.isRecording;
    }

    /**
     * Check if recording is paused
     */
    isPaused(): boolean {
        return this.isPausedFlag;
    }

    /**
     * Get current chunk index
     */
    getCurrentChunkIndex(): number {
        return this.chunkIndex;
    }
}
